import axios from 'axios';
import { User, Debt } from '../../types';
import { getAuthHeader } from '../authService';
import { API_BASE_URL } from '../../utils/constants';

// Configure axios to include auth headers
const getAxiosConfig = () => ({
  headers: getAuthHeader()
});

// Get all users (created by logged-in user)
export const getUsers = async (userId: string): Promise<User[]> => {
  try {
    if (!userId) {
      console.error('User ID not provided');
      return [];
    }
    const response = await axios.get(
      `${API_BASE_URL}/users/created-by/${userId}`,
      getAxiosConfig()
    );
    
    // Handle API response format: {success, message, data}
    if (response.data && response.data.data) {
      return Array.isArray(response.data.data) ? response.data.data : [];
    }
    
    // Fallback: if response.data is already an array
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    console.error('Error fetching users:', error);
    return [];
  }
};

// Get user by ID
export const getUserById = async (id: number): Promise<User | null> => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/users/${id}`,
      getAxiosConfig()
    );
    
    // Handle API response format: {success, message, data}
    if (response.data && response.data.data) {
      return response.data.data;
    }
    
    // Fallback: if response.data is the user object directly
    return response.data;
  } catch (error) {
    console.error('Error fetching user by ID:', error);
    return null;
  }
};

// Download user template
export const downloadUserTemplate = async (): Promise<Blob> => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/users/template/download`,
      {
        ...getAxiosConfig(),
        responseType: 'blob',
      }
    );
    return response.data;
  } catch (error: any) {
    console.error('Error downloading user template:', error);
    
    // Re-throw with better error information
    if (error.response?.status === 500 || error.response?.status === 400) {
      throw error;
    }
    
    throw new Error('Failed to download template. Please check your connection.');
  }
};

// Upload users file
export const uploadUsers = async (file: File): Promise<void> => {
  const formData = new FormData();
  formData.append('file', file);
  
  await axios.post(
    `${API_BASE_URL}/users/template/upload`,
    formData,
    {
      headers: {
        ...getAuthHeader(),
        'Content-Type': 'multipart/form-data',
      },
    }
  );
};

// Get debts for a user
export const getUserDebts = async (userId: number): Promise<Debt[]> => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/users/${userId}/debts`,
      getAxiosConfig()
    );
    
    // Handle API response format: {success, message, data}
    if (response.data && response.data.data) {
      // The data might have a 'debts' property or be the array directly
      if (response.data.data.debts) {
        return Array.isArray(response.data.data.debts) ? response.data.data.debts : [];
      }
      return Array.isArray(response.data.data) ? response.data.data : [];
    }
    
    // Fallback: if response.data is already an array
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    console.error('Error fetching user debts:', error);
    return [];
  }
};


